/**
 * CRM Service - HubSpot-style contact and pipeline management
 *
 * Pipeline stages:
 * - lead: Initial contact
 * - qualified: Qualified lead
 * - proposal: Proposal sent
 * - negotiation: In negotiation
 * - customer: Converted customer
 * - churned: Lost/churned
 */

import type { Contact, ContactStage } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getEntitiesByWorkspace,
  updateEntity,
  deleteEntity,
} from '../store';
import { generateId } from '../id';

export interface CreateContactInput {
  workspaceId: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  role?: string;
  stage?: ContactStage;
  notes?: string;
  tags?: string[];
  customFields?: Record<string, string>;
  ownerId?: string;
  dealValue?: number;
  source?: string;
  avatar?: string;
}

export function createContact(input: CreateContactInput): Contact {
  const now = new Date();
  const contact: Contact = {
    id: generateId('contact'),
    workspaceId: input.workspaceId,
    name: input.name,
    email: input.email,
    phone: input.phone,
    company: input.company,
    role: input.role,
    stage: input.stage ?? 'lead',
    notes: input.notes ?? '',
    tags: input.tags ?? [],
    customFields: input.customFields ?? {},
    ownerId: input.ownerId,
    dealValue: input.dealValue,
    source: input.source,
    avatar: input.avatar,
    lastContactedAt: now,
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().contacts, contact);
}

export function getContact(id: string): Contact | undefined {
  return getEntity(getStore().contacts, id);
}

export function getContactsByWorkspace(workspaceId: string): Contact[] {
  return getEntitiesByWorkspace(getStore().contacts, workspaceId);
}

export function getContactsByStage(workspaceId: string, stage: ContactStage): Contact[] {
  return getContactsByWorkspace(workspaceId).filter((c) => c.stage === stage);
}

export function getContactsByOwner(workspaceId: string, ownerId: string): Contact[] {
  return getContactsByWorkspace(workspaceId).filter((c) => c.ownerId === ownerId);
}

export function searchContacts(workspaceId: string, query: string): Contact[] {
  const contacts = getContactsByWorkspace(workspaceId);
  const lowerQuery = query.toLowerCase();
  return contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(lowerQuery) ||
      contact.email?.toLowerCase().includes(lowerQuery) ||
      contact.company?.toLowerCase().includes(lowerQuery) ||
      contact.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}

export function updateContact(
  id: string,
  updates: Partial<Omit<Contact, 'id' | 'workspaceId' | 'createdAt'>>
): Contact | undefined {
  return updateEntity(getStore().contacts, id, updates);
}

export function updateContactStage(id: string, stage: ContactStage): Contact | undefined {
  return updateContact(id, { stage });
}

export function assignContactOwner(id: string, ownerId: string | undefined): Contact | undefined {
  return updateContact(id, { ownerId });
}

export function updateDealValue(id: string, dealValue: number): Contact | undefined {
  return updateContact(id, { dealValue });
}

export function recordContactInteraction(id: string): Contact | undefined {
  return updateContact(id, { lastContactedAt: new Date() });
}

export function deleteContact(id: string): boolean {
  return deleteEntity(getStore().contacts, id);
}

// Move contact to next stage in pipeline
export function moveContactForward(id: string): Contact | undefined {
  const contact = getContact(id);
  if (!contact) return undefined;

  const stages: ContactStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'customer'];
  const currentIndex = stages.indexOf(contact.stage);

  if (currentIndex >= 0 && currentIndex < stages.length - 1) {
    return updateContactStage(id, stages[currentIndex + 1]);
  }
  return contact;
}

// Move contact to previous stage in pipeline
export function moveContactBackward(id: string): Contact | undefined {
  const contact = getContact(id);
  if (!contact) return undefined;

  const stages: ContactStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'customer'];
  const currentIndex = stages.indexOf(contact.stage);

  if (currentIndex > 0) {
    return updateContactStage(id, stages[currentIndex - 1]);
  }
  return contact;
}

export function getContactStats(workspaceId: string): Record<ContactStage, number> {
  const contacts = getContactsByWorkspace(workspaceId);
  const stages: ContactStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'customer', 'churned'];

  return stages.reduce((acc, stage) => {
    acc[stage] = contacts.filter((c) => c.stage === stage).length;
    return acc;
  }, {} as Record<ContactStage, number>);
}

export function getTotalDealValue(workspaceId: string): number {
  const contacts = getContactsByWorkspace(workspaceId);
  return contacts.reduce((total, c) => total + (c.dealValue ?? 0), 0);
}

export function getPipelineDealValue(workspaceId: string): Record<ContactStage, number> {
  const contacts = getContactsByWorkspace(workspaceId);
  const stages: ContactStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'customer', 'churned'];

  return stages.reduce((acc, stage) => {
    acc[stage] = contacts
      .filter((c) => c.stage === stage)
      .reduce((total, c) => total + (c.dealValue ?? 0), 0);
    return acc;
  }, {} as Record<ContactStage, number>);
}

export function getNewLeadsThisWeek(workspaceId: string): Contact[] {
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);

  return getContactsByWorkspace(workspaceId).filter(
    (contact) =>
      contact.stage === 'lead' && new Date(contact.createdAt) >= weekAgo
  );
}

export function getContactsNeedingFollowUp(workspaceId: string, daysThreshold = 7): Contact[] {
  const threshold = new Date();
  threshold.setDate(threshold.getDate() - daysThreshold);

  return getContactsByWorkspace(workspaceId).filter(
    (contact) =>
      contact.stage !== 'customer' &&
      contact.stage !== 'churned' &&
      contact.lastContactedAt &&
      new Date(contact.lastContactedAt) < threshold
  );
}

export function getCrmSummary(workspaceId: string): {
  total: number;
  byStage: Record<ContactStage, number>;
  totalDealValue: number;
  newLeadsThisWeek: number;
} {
  return {
    total: getContactsByWorkspace(workspaceId).length,
    byStage: getContactStats(workspaceId),
    totalDealValue: getTotalDealValue(workspaceId),
    newLeadsThisWeek: getNewLeadsThisWeek(workspaceId).length,
  };
}
